import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { graphql } from '@/gql';
import type {
  TicketSalesInterval,
  TicketSalesSeriesQuery,
  TicketSalesSeriesQueryVariables,
} from '@/gql/graphql';

/** The ticket sales chart's series query: one series per night and stage, on a shared "N before doors close" axis. */
export const TicketSalesSeriesDoc = graphql(/* GraphQL */ `
  query TicketSalesSeries($nightIds: [UUID!]!, $interval: TicketSalesInterval!) {
    ticketSalesSeries(nightIds: $nightIds, interval: $interval) {
      categories
      series {
        nightId
        nightName
        stageId
        stageName
        data
        pending
      }
    }
  }
`) as TypedDocumentNode<TicketSalesSeriesQuery, TicketSalesSeriesQueryVariables>;

const UNIT: Record<TicketSalesInterval, string> = { HOUR: 'hours', DAY: 'days', WEEK: 'weeks' };

/** The x label of a bucket: units before doors close, 0 = the doors-close bucket itself. */
export function bucketLabel(n: number, interval: TicketSalesInterval): string {
  if (n === 0) return 'Doors close';
  const unit = n === 1 ? UNIT[interval].slice(0, -1) : UNIT[interval];
  return `${n} ${unit} before`;
}

/** A running total of per-bucket counts. */
export function cumulate(data: readonly number[]): number[] {
  let running = 0;
  return data.map((v) => (running += v));
}

/**
 * What a line or bar series draws: the buckets (running totals when `cumulative`) up to today. The last `pending`
 * buckets are still to come while the night is on sale: null draws nothing there (a line ends, a bar is absent). The
 * bucket in progress is not pending and is drawn. Shared by the Charts page and the night's Ticket sales card.
 */
export function drawnSeries(data: readonly number[], pending: number, cumulative: boolean): Array<number | null> {
  const shown = cumulative ? cumulate(data) : data;
  const cut = Math.max(0, shown.length - pending);
  return shown.map((v, i) => (i < cut ? v : null));
}
